import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { isTauri } from "@/lib/platform";

export const MCP_CLIENTS = ["remote", "claude-code", "claude-desktop", "cursor"] as const;
export type McpClient = (typeof MCP_CLIENTS)[number];

export const MCP_CLIENT_LABELS: Record<McpClient, string> = {
  // Added in Claude's own settings, not here: the connector runs in Anthropic's
  // cloud, so there is no local config for the app to write.
  remote: "Claude web & mobile",
  "claude-code": "Claude Code",
  "claude-desktop": "Claude Desktop",
  cursor: "Cursor",
};

/** Claude Code owns its own config format, so only these two can be written. */
export const WRITABLE_CLIENTS: McpClient[] = ["claude-desktop", "cursor"];

export type McpConnection = { client: string; lastSeenAt: string; firstSeenAt: string };

/**
 * When a server counts as live.
 *
 * The heartbeat is written on startup and then at most once a minute while
 * tools are being called, so a gap of a few minutes means the editor is open
 * but idle — still connected. Beyond that it is a server that ran once, which
 * is a different thing and should not be shown as green.
 */
const LIVE_MS = 10 * 60_000;

export type ConnectionState = "live" | "stale" | "never";

export function stateOf(conn: McpConnection | undefined): ConnectionState {
  if (!conn) return "never";
  return Date.now() - new Date(conn.lastSeenAt).getTime() < LIVE_MS ? "live" : "stale";
}

export function useMcpStatus() {
  return useQuery({
    queryKey: ["mcp", "status"],
    queryFn: () => api<{ connections: McpConnection[] }>("/v1/mcp/status"),
    select: (r) => new Map(r.connections.map((c) => [c.client, c])),
    // The interesting moment is right after someone restarts their editor, so
    // this refetches rather than making them reload the page to see it turn green.
    refetchInterval: 15_000,
  });
}

/** Which clients the desktop app can write to, and whether it has a template. */
export type TauriConfigured = { configured: string[]; can_connect: boolean };

export function useTauriConfigured() {
  return useQuery({
    queryKey: ["mcp", "configured"],
    enabled: isTauri(),
    queryFn: async (): Promise<TauriConfigured> => {
      const { invoke } = await import("@tauri-apps/api/core");
      return invoke<TauriConfigured>("mcp_configured");
    },
    staleTime: 5_000,
  });
}

export function useTauriConnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { client: McpClient; userId: string; disconnect?: boolean }) => {
      const { invoke } = await import("@tauri-apps/api/core");
      return invoke<string>(input.disconnect ? "mcp_disconnect" : "mcp_connect", {
        client: input.client,
        userId: input.userId,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["mcp"] });
    },
  });
}

/** What you paste into Claude's "Add custom connector" box. */
export const MCP_CONNECTOR_URL = "https://sebas.akhsanhakiki.com/mcp";

export const connectCommand = (client: McpClient, userId: string): string =>
  `bun run --filter @sebas/mcp connect ${client} --user ${userId}`;

/** "just now" / "4m ago" / "2d ago" — enough precision for a status line. */
export function since(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
